import {
  Address,
  BUILDING_TYPE,
  CHANGE_TYPE,
  ChangeOtherDetailsDto,
  ChargingDeviceDetailsDto,
  ConstructionElectricity,
  ContactDetails,
  CONTROLLABLE_POWER,
  ControllableDetailsDto,
  Document,
  FORM_OF_FEED_IN_SALE,
  FORM_TYPE,
  GAS_OPTION,
  GasBuildingDetails,
  GRID_CONNECTION_CONSUMER_TYPE,
  GRID_CONNECTION_TYPE,
  HEAT_OPTION,
  HeatBuildingDetails,
  HeatPumpInformationDto,
  NETWORK_AND_SYSTEM_PROTECTION,
  NetworkAndSystemProtectionDetailsDto,
  ON_BEHALF_TYPE,
  OTHER_TYPE,
  OtherSystemDetailsDto,
  PowerChangeDetails,
  PV_INSTALLATION_PLACE,
  PVCustomerFacilityDto,
  PVInverterDto,
  SEWAGE_OPTION,
  SEWAGE_TYPE,
  StorageDetails,
  StorageDetailsDto,
  WATER_OPTION,
  WaterBuildingDetails,
} from "@app/models/registration-form";

import {
  COMMISSIONING_FLOW_TYPE,
  ELECTRICITY_SYSTEM_TYPE,
  MainPowerSupplyPowerIncrease,
  MAIN_POWER_SUPPLY_TYPE,
  MeasurementDetails,
} from "./commissioning-mapped-data.interface";
import {
  PartialFormData,
  PartialOtherData,
} from "../services/app-state.service";

interface AdditionalAddressData {
  district?: string;
  parcel?: string;
  parcelNumber?: string;
}

export enum FLOW {
  REGISTRATION = "REGISTRATION",
  COMMISSIONING = "COMMISSIONING",
}

export enum FLOW_MODULE {
  GAS = "GAS",
  ELECTRICITY = "ELECTRICITY",
  WATER = "WATER",
  HEAT = "HEAT",
}

export type WATER_AREA = Extract<
  FORM_TYPE,
  FORM_TYPE.WATER | FORM_TYPE.SEWAGE | FORM_TYPE.CONSTRUCTION_WATER
>;

export interface ExtendedAddress extends Address {
  streetNotListed: boolean;
  additionalAddressData?: AdditionalAddressData;
}

export interface DocumentWithFile extends Document {
  file: File;
}

export interface FormContact {
  basicData: ContactDetails;
  hasDifferentAddress: boolean;
  differentAddress?: Address;
  role: ON_BEHALF_TYPE;
  // required when role is not CONNECTION_HOLDER
  onBehalfContactDetails?: {
    basicData: ContactDetails;
    address: Address;
    powerOfAttorney: boolean;
    powerOfAttorneyDocument?: DocumentWithFile;
  };
  // required when role is INSTALLER
  installerDetails?: {
    gridOperator: string;
    registrationNumber: string;
    companyName: string;
  };
  // required when when role is AUTHORIZED_REPRESENTATIVE
  authorizedRepresentativeRole?: string;
}

export interface DocumentDetailsWithFile {
  documents: DocumentWithFile[];
  notes?: string;
}

export interface PvInverter extends PVInverterDto {
  maxApparentPowerSum: number;
  maxControllablePowerSum: number;
}
export interface PvGenerationUnit {
  powerPerModule: number;
  moduleCount: number;
  installationPlace: PV_INSTALLATION_PLACE;
  powerFlowMonitoring?: boolean;
  powerFlowMonitoringManufacturer?: string;
  powerFlowMonitoringType?: string;
  powerFlowMonitoringPower?: number;
  networkAndSystemProtectionDetailsLocation?: NETWORK_AND_SYSTEM_PROTECTION;
  networkAndSystemProtectionDetailsManufacturer?: string;
  networkAndSystemProtectionDetailsType?: string;
}

export interface BalconyPVInfo {
  modulePower: boolean;
  inverterPower: boolean;
  freeFeedIn: boolean;
  registrationRequired: boolean;
}

export interface ChargingDeviceSystemInformation {
  plannedCommissioningDate?: string;
  controllable: boolean;
}

export interface HeatPumpSystemInformation {
  plannedCommissioningDate?: string;
  controllable: boolean;
}

type GridConnectionConsumerType = Partial<{
  [K in GRID_CONNECTION_CONSUMER_TYPE]: boolean;
}>;

export interface GridConnectionConsumer {
  type: GridConnectionConsumerType;
  connectionFuse: number;
  systemPower: number;
  maxConcurrentPower: number;
  additionalConsumers: string;
}

export interface FormNewGridConnectionDetails {
  buildingType: BUILDING_TYPE;
  basementAvailable: boolean;
  externalConnection: boolean;
  flatCount?: number;
  area?: number;
}

export interface FormChangeGridConnectionDetails {
  changeType: CHANGE_TYPE;
  totalPowerChanged: boolean;
  ditchLength: number;
}

export interface HeatPump {
  systemManufacturer?: string;
  systemType?: string;
  systemPower: number;
  meterRequired: boolean;
  meterNumber?: string;
  connectionPowerIncrease: boolean;
  currentPowerDemand?: number;
  plannedPowerDemand?: number;
  controllablePower: CONTROLLABLE_POWER;
  notes?: string;
}

export interface GasOptions {
  gasOption: GAS_OPTION;
  changePower?: boolean;
  relocate?: boolean;
  currentPowerDemand?: number;
  plannedPowerDemand?: number;
  ditchLength?: number;
  partialDeconstructionDesired?: boolean;
  meterNumber?: string;
}

export interface GasPowerRequirement {
  powerDemand: number;
  notes?: string;
}
export interface WaterRequirementDetails {
  numberOfHousingUnits: number;
  numberOfResidents?: number;
  numberOfFloors: number;
  extractionPointHeight: number;
  flowRate?: number;
  additionalConsumers?: string;
}

export interface ConstructionWaterRequirements {
  flowRate?: number;
  convertedSpace: number;
  desiredInstallationDate: Date;
  desiredDeconstructionDate?: Date;
  notes?: string;
}

export interface WaterOptions {
  waterOption: WATER_OPTION;
  changeFlowRate?: boolean;
  relocate?: boolean;
  currentFlowRate?: number;
  plannedFlowRate?: number;
  additionalDemand?: string;
  ditchLength?: number;
  permanentDeconstruction?: boolean;
  meterNumber?: string;
  deconstructionUntil?: Date;
}

export interface SewageOptions {
  sewageOption: SEWAGE_OPTION;
  permanentDeconstruction?: boolean;
  meterNumber?: string;
  deconstructionUntil?: Date;
}

export interface HeatOptions {
  heatOption: HEAT_OPTION;
  changeHeatPower?: boolean;
  changeOther?: boolean;
  currentHeatPower?: number;
  plannedHeatPower?: number;
  additionalHeatDemand?: string;
  otherChanges?: string;
  permanentDeconstruction?: boolean;
  meterNumber?: string;
  deconstructionUntil?: Date;
}

export interface HeatRequirementDetails {
  heatOutputDemand: number;
  otherConsumers?: string;
  otherConsumersPower?: number;
  notes?: string;
}

export interface CommissioningElectricityBuildingDetails {
  buildingType: BUILDING_TYPE;
  directMeasurementMeterLocationsCount?: number;
  transformerMeasurementMeterLocationsCount?: number;
  flatCount?: number;
  area?: number;
}

export interface CommissioningElectricityInformation {
  systemPower: number;
  connectionFuse: number;
  constructionElectricity: boolean;
}

export interface SystemDetails {
  selected: boolean;
  systemPower?: number;
  systemCount?: number;
  systemType?: string;
  notes?: string;
}

export interface CommissioningElectricitySystems {
  [ELECTRICITY_SYSTEM_TYPE.PV]: SystemDetails;
  [ELECTRICITY_SYSTEM_TYPE.STORAGE]: SystemDetails;
  [ELECTRICITY_SYSTEM_TYPE.CHARGING_DEVICE]: SystemDetails;
  [ELECTRICITY_SYSTEM_TYPE.HEAT_PUMP]: SystemDetails;
  [ELECTRICITY_SYSTEM_TYPE.OTHER]: SystemDetails;
  meterChanges: boolean;
}

export interface CommissioningElectricitySimpleMeasurementDetails {
  meterNumbers: string[];
}

export interface CommissioningElectricityContact {
  requesterDetails: {
    basicData: ContactDetails;
    hasDifferentAddress: boolean;
    differentAddress?: Address; // required if hasDifferentAddress is true
  };
  electricianDetails: {
    basicData: ContactDetails;
    gridOperator: string;
    registrationNumber: string;
    companyName: string;
    address: Address;
    date: Date;
    powerOfAttorney: boolean;
    powerOfAttorneyDocument: DocumentWithFile;
  };
}

export type OtherCustomerFacility = {
  controllable: boolean;
  plannedCommissioningDate?: string;
};

export type StorageCustomerFacility = {
  controllable: boolean;
  plannedCommissioningDate?: string;
  formOfFeedInSale: FORM_OF_FEED_IN_SALE;
};

export interface FormDataObject {
  address?: ExtendedAddress;
  selectedModule?: FLOW_MODULE;
  selectedFlow?: FLOW;
  documentDetails?: DocumentDetailsWithFile;
  contact?: FormContact;
  balconyPVInfo?: BalconyPVInfo;
  formType?: FORM_TYPE | null;
  isBalconyPV?: boolean;
  pvCustomerFacility?: PVCustomerFacilityDto;
  pvInverter?: PvInverter;
  pvGenerationUnit?: PvGenerationUnit;
  storageDetails?: StorageDetails;
  storageCustomerFacility?: StorageCustomerFacility;
  storageDetailsDto?: StorageDetailsDto;
  storageControllableDetails?: ControllableDetailsDto;
  storageNetworkAndSystemProtectionDetails?: NetworkAndSystemProtectionDetailsDto;
  chargingDeviceSystemInformation?: ChargingDeviceSystemInformation;
  chargingDeviceControllableDetails?: ControllableDetailsDto;
  chargingDeviceInformation?: ChargingDeviceDetailsDto;
  gridConnectionType?: GRID_CONNECTION_TYPE;
  gridConnectionConsumer?: GridConnectionConsumer;
  powerChangeDetails?: PowerChangeDetails;
  newGridConnectionDetails?: FormNewGridConnectionDetails;
  changeGridConnectionDetails?: FormChangeGridConnectionDetails;
  constructionElectricity?: ConstructionElectricity;
  heatPumpSystemInformation?: HeatPumpSystemInformation;
  heatPumpControllableDetails?: ControllableDetailsDto;
  heatPumpInformation?: HeatPumpInformationDto;
  otherType?: OTHER_TYPE;
  otherCustomerFacility?: OtherCustomerFacility;
  otherControllableDetails?: ControllableDetailsDto;
  otherSystemDetails?: OtherSystemDetailsDto;
  otherDetails?: Omit<ChangeOtherDetailsDto, "type">;
  gasOptions?: GasOptions;
  gasBuildingDetails?: GasBuildingDetails;
  gasPowerRequirement?: GasPowerRequirement;
  sewageOptions?: SewageOptions;
  sewageType?: SEWAGE_TYPE;
  waterOptions?: WaterOptions;
  waterArea?: WATER_AREA;
  constructionWaterRequirements?: ConstructionWaterRequirements;
  waterBuildingDetails?: WaterBuildingDetails;
  waterRequirementDetails?: WaterRequirementDetails;
  heatOptions?: HeatOptions;
  heatBuildingDetails?: HeatBuildingDetails;
  heatRequirementDetails?: HeatRequirementDetails;
  commissioningFlowType?: COMMISSIONING_FLOW_TYPE;
  mainPowerSupplyType?: MAIN_POWER_SUPPLY_TYPE;
  commissioningElectricityBuildingDetails?: CommissioningElectricityBuildingDetails;
  commissioningElectricityInformation?: CommissioningElectricityInformation;
  mainPowerSupplyPowerIncreaseDetails?: MainPowerSupplyPowerIncrease;
  commissioningElectricitySystems?: CommissioningElectricitySystems;
  commissioningElectricitySimpleMeasurementDetails?: CommissioningElectricitySimpleMeasurementDetails;
  commissioningElectricityMeasurementDetails?: MeasurementDetails[];
  commissioningElectricityContact?: CommissioningElectricityContact;
}

export interface OtherData {
  productSelectionVisited?: boolean;
  responsibilityCheckFail?: boolean;
}

export interface AppState {
  formData: PartialFormData;
  otherData: PartialOtherData;
}
